<template>
  <LoadSpinner v-if="loading" />
  <div
    v-else-if="showAsCarousel"
    class="housing-locality-group-carousel"
  >
    <div
      ref="viewportEl"
      class="housing-locality-group-carousel__viewport"
    >
      <div
        ref="swiperEl"
        class="swiper housing-locality-group-carousel__swiper"
      >
        <div class="swiper-wrapper">
          <div
            v-for="item in items"
            :key="itemKey(item)"
            class="swiper-slide housing-locality-group-carousel__slide"
          >
            <ApartmentCard
              v-if="isApartment"
              :apartment="item"
              :hide-buttons="hideButtons"
              class="housing-locality-group-carousel__item"
            />
            <HousingLocalityCard
              v-else
              :housing-locality="item"
              class="housing-locality-group-carousel__item"
            />
          </div>
        </div>
      </div>

      <button
        v-show="showNav"
        type="button"
        class="housing-locality-group-carousel__nav housing-locality-group-carousel__nav--prev"
        :aria-label="translate('Edellinen')"
        @click="goPrev"
      >
        <ChevronIcon class="housing-locality-group-carousel__nav-icon housing-locality-group-carousel__nav-icon--prev" />
      </button>
      <button
        v-show="showNav"
        type="button"
        class="housing-locality-group-carousel__nav housing-locality-group-carousel__nav--next"
        :aria-label="translate('Seuraava')"
        @click="goNext"
      >
        <ChevronIcon class="housing-locality-group-carousel__nav-icon" />
      </button>
    </div>

    <div
      v-show="showNav"
      ref="paginationEl"
      class="housing-locality-group-carousel__pagination"
      :class="{ 'housing-locality-group-carousel__pagination--hidden': !showPagination }"
    />
  </div>
  <div
    v-else
    class="listing-card-grid"
  >
    <div class="listing-card-grid__grid">
      <div
        v-for="item in items"
        :key="itemKey(item)"
        class="listing-card-grid__column"
      >
        <ApartmentCard
          v-if="isApartment"
          :apartment="item"
          :hide-buttons="hideButtons"
          class="listing-card-grid__item"
        />
        <HousingLocalityCard
          v-else
          :housing-locality="item"
          class="listing-card-grid__item"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import Swiper from 'swiper';
  import { Pagination } from 'swiper/modules';

  import globals from '../lib/globals';
  import utils from './mixins/utils';
  import LoadSpinner from './components/LoadSpinner';
  import HousingLocalityCard from './components/HousingLocalityCard';
  import ApartmentCard from './components/ApartmentCard';
  import ChevronIcon from './icons/ChevronIcon.vue';

  // Match theme grid breakpoints (md / xl). Swiper breakpoints use window width by default.
  const BREAKPOINT_MD = 768;
  const BREAKPOINT_XL = 1200;
  // Match SCSS r(10) / r(8) tokens used for bullets + gap.
  const BULLET_SIZE_TOKEN = 10;
  const BULLET_GAP_TOKEN = 8;

  export default {
    components: {
      HousingLocalityCard,
      ApartmentCard,
      LoadSpinner,
      ChevronIcon,
    },
    mixins: [
      utils.translate,
    ],
    props: {
      contentType: {
        type: String,
        default: 'housing_locality',
      },
      selectionMode: {
        type: String,
        default: 'filter',
      },
      group: {
        type: String,
        default: 'old',
      },
      types: {
        type: String,
        default: 'all',
      },
      locationText: {
        type: String,
        default: '',
      },
      campaignCode: {
        type: String,
        default: '',
      },
      roomCounts: {
        type: Array,
        default: () => [],
      },
      availability: {
        type: Array,
        default: () => [],
      },
      housingLocalityIds: {
        type: Array,
        default: () => [],
      },
      pickedIds: {
        type: Array,
        default: () => [],
      },
      showAsCarousel: {
        type: Boolean,
        default: false,
      },
      hideButtons: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        loading: false,
        items: [],
        swiperInstance: null,
        showNav: false,
        showPagination: true,
        isReinitializing: false,
      };
    },
    computed: {
      isApartment() {
        return this.contentType === 'apartment';
      },
    },
    async beforeMount() {
      this.loading = true;
      try {
        this.items = await this.fetchItems();
      } catch (e) {
        console.error(e);
        this.items = [];
      }
      this.loading = false;
    },
    watch: {
      loading(isLoading) {
        if (!isLoading && this.showAsCarousel && this.items.length) {
          this.$nextTick(() => this.initSwiper());
        }
      },
    },
    beforeUnmount() {
      this.destroySwiper();
    },
    methods: {
      itemKey(item) {
        if (this.isApartment) {
          return item.mpc_asuntoid || item.slug || item.id;
        }
        return item.mpc_kohdeid || item.slug || item.id;
      },
      async fetchItems() {
        if (this.selectionMode === 'pick') {
          return this.fetchPicked();
        }
        return this.isApartment
          ? this.fetchApartmentsFilter()
          : this.fetchLocalitiesFilter();
      },
      async fetchPicked() {
        if (!this.pickedIds.length) {
          return [];
        }

        // Apartments: POST body (URL length + established theme pattern in getApartment.js).
        if (this.isApartment) {
          const response = await axios.post(
            `${globals.urlIntegrationApi}/public/apartments`,
            { apartments: this.pickedIds },
          );
          return response.data || [];
        }

        // Housing localities: POST body (same as apartments — long GET UUID lists get truncated).
        const response = await axios.post(
          `${globals.urlIntegrationApi}/public/housinglocalities`,
          { housinglocalities: this.pickedIds },
        );
        return response.data || [];
      },
      async fetchLocalitiesFilter() {
        const response = await axios.post(
          `${globals.urlIntegrationApi}/public/housinglocalities-filter`,
          {
            group: this.group,
            types: this.types,
            location_text: this.locationText,
            campaign_code: this.campaignCode,
          },
        );
        return response.data || [];
      },
      async fetchApartmentsFilter() {
        const response = await axios.post(
          `${globals.urlIntegrationApi}/public/apartments-listing-filter`,
          {
            group: this.group,
            types: this.types === 'all' ? [] : this.types.split(',').filter(Boolean),
            location_text: this.locationText,
            campaign_code: this.campaignCode,
            room_counts: this.roomCounts,
            availability: this.availability,
            housing_locality_ids: this.housingLocalityIds,
          },
        );
        return response.data || [];
      },
      slidesPerViewForWidth(width) {
        if (width >= BREAKPOINT_XL) {
          return 3;
        }
        if (width >= BREAKPOINT_MD) {
          return 2;
        }
        return 1;
      },
      getSlidesPerView() {
        if (this.swiperInstance) {
          const spv = this.swiperInstance.params.slidesPerView;
          if (typeof spv === 'number') {
            return spv;
          }
        }
        return this.slidesPerViewForWidth(window.innerWidth);
      },
      updateShowNav() {
        this.showNav = this.items.length > this.getSlidesPerView();
      },
      updatePaginationVisibility() {
        if (!this.showNav || !this.swiperInstance) {
          this.showPagination = false;
          return;
        }

        const count = this.swiperInstance.snapGrid?.length || 0;
        if (count <= 1) {
          this.showPagination = false;
          return;
        }

        const available = this.$refs.swiperEl?.clientWidth || 0;
        if (!available) {
          this.showPagination = false;
          return;
        }

        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const bulletSize = (BULLET_SIZE_TOKEN / 16) * rootFontSize;
        const gap = (BULLET_GAP_TOKEN / 16) * rootFontSize;
        const needed = (count * bulletSize) + ((count - 1) * gap);

        this.showPagination = needed <= available;
      },
      updateArrowPosition() {
        const viewport = this.$refs.viewportEl;
        const image = this.$refs.swiperEl?.querySelector('.base-card__image');
        const prev = viewport?.querySelector('.housing-locality-group-carousel__nav--prev');
        const next = viewport?.querySelector('.housing-locality-group-carousel__nav--next');

        if (!viewport || !image || !prev || !next) {
          return;
        }

        const viewportRect = viewport.getBoundingClientRect();
        const imageRect = image.getBoundingClientRect();
        const top = imageRect.top - viewportRect.top + (imageRect.height / 2);

        prev.style.top = `${top}px`;
        next.style.top = `${top}px`;
      },
      goNext() {
        const swiper = this.swiperInstance;
        if (!swiper) {
          return;
        }
        if (swiper.isEnd) {
          swiper.slideTo(0);
        } else {
          swiper.slideNext();
        }
      },
      goPrev() {
        const swiper = this.swiperInstance;
        if (!swiper) {
          return;
        }
        if (swiper.isBeginning) {
          swiper.slideTo(swiper.slides.length - 1);
        } else {
          swiper.slidePrev();
        }
      },
      onSwiperLayoutChange() {
        if (this.isReinitializing) {
          return;
        }

        const prevShowNav = this.showNav;
        this.updateShowNav();
        this.updateArrowPosition();
        this.updatePaginationVisibility();

        if (prevShowNav !== this.showNav) {
          this.isReinitializing = true;
          this.$nextTick(() => {
            this.initSwiper();
            this.isReinitializing = false;
          });
        }
      },
      initSwiper() {
        this.destroySwiper();

        if (!this.$refs.swiperEl) {
          return;
        }

        this.updateShowNav();

        const enableNav = this.showNav;

        this.swiperInstance = new Swiper(this.$refs.swiperEl, {
          modules: [Pagination],
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 24,
          loop: false,
          rewind: false,
          watchOverflow: true,
          grabCursor: enableNav,
          pagination: {
            el: this.$refs.paginationEl,
            clickable: true,
          },
          breakpoints: {
            [BREAKPOINT_MD]: {
              slidesPerView: 2,
              slidesPerGroup: 1,
              spaceBetween: 24,
            },
            [BREAKPOINT_XL]: {
              slidesPerView: 3,
              slidesPerGroup: 1,
              spaceBetween: 24,
            },
          },
          on: {
            init: () => {
              this.$nextTick(() => {
                this.updateShowNav();
                this.updateArrowPosition();
                this.updatePaginationVisibility();
              });
            },
            resize: () => {
              this.onSwiperLayoutChange();
            },
            breakpoint: () => {
              this.onSwiperLayoutChange();
            },
            slidesLengthChange: () => {
              this.updatePaginationVisibility();
            },
          },
        });
      },
      destroySwiper() {
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      },
    },
  };
</script>

<style lang="scss">
  @import "~styles/definitions";

  $carousel-nav-size: r(48);
  $carousel-bullet-size: r(10);

  .housing-locality-group-carousel {
    @include prevent-margin-collapse;
  }

  .housing-locality-group-carousel__viewport {
    position: relative;
  }

  .housing-locality-group-carousel__swiper {
    width: 100%;
    overflow: hidden;
  }

  // Swiper's CSS sets box-sizing: content-box on the wrapper and slides, and the
  // theme's `*` reset inherits it, which breaks card buttons (extra padding, overflow).
  .housing-locality-group-carousel__swiper .swiper-wrapper,
  .housing-locality-group-carousel__swiper .swiper-slide {
    box-sizing: border-box;
  }

  .housing-locality-group-carousel__slide {
    height: auto;
    display: flex;
  }

  .housing-locality-group-carousel__item {
    flex: 1 0 100%;
    width: 100%;
    min-width: 0;

    .link-with-icon {
      width: auto;
      align-self: center;
      margin: $theme-spacing $theme-spacing-2 $theme-spacing-2;
      max-width: calc(100% - #{$theme-spacing-2} * 2);
      box-sizing: border-box;
    }
  }

  .housing-locality-group-carousel__nav {
    position: absolute;
    z-index: 2;
    top: 0;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    width: $carousel-nav-size;
    height: $carousel-nav-size;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: $color-green-light-2025;
    color: $color-green-2025;
    cursor: pointer;
    transition: background-color 100ms ease-in-out, color 100ms ease-in-out;

    &:hover,
    &:focus {
      background-color: $color-green-light-2025-hover;
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid $color-green-2025;
      outline-offset: 2px;
    }
  }

  .housing-locality-group-carousel__nav--prev {
    left: 0;
  }

  .housing-locality-group-carousel__nav--next {
    left: auto;
    right: 0;
    transform: translate(50%, -50%);
  }

  .housing-locality-group-carousel__nav-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: r(24);
    height: r(24);
    line-height: 0;

    &--prev {
      transform: scaleX(-1);
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 0;
    }

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  .housing-locality-group-carousel__pagination {
    display: flex;
    flex-wrap: nowrap;
    justify-content: center;
    align-items: center;
    gap: r(8);
    margin-top: r(24);
    position: static;
    width: 100%;

    &--hidden {
      display: none;
    }

    .swiper-pagination-bullet {
      flex: 0 0 auto;
      width: $carousel-bullet-size;
      height: $carousel-bullet-size;
      margin: 0;
      opacity: 1;
      background-color: $color-green-light-2025;
      transition: background-color 100ms ease-in-out;
    }

    .swiper-pagination-bullet-active {
      background-color: $color-green-2025;
    }
  }
</style>
